// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { Component } from '../component';
import { render, prepareUrl, showInternalError, addStatusMessage, clearStatusMessages, createElement } from '../common';

/**
 * @class Jsw.FileManager.Tree
 * The widget displays directories and files in the webspace, allows selection
 * @extends Jsw.Component
 *
 * Example usage:
 *
 *     @example
 *     new Jsw.FileManager.Tree({
 *         renderTo: document.body,
 *         rootNodeTitle: 'Root node',
 *         data: [{
 *             name: 'folder',
 *             isDirectory: true,
 *             icon: '/icons/16/plesk/file-folder.png'
 *         }, {
 *             name: 'file.html',
 *             isDirectory: false,
 *             icon: '/icons/16/plesk/file-html.png'
 *         }]
 *     });
 */
export const Tree = Class.create(Component, {
    /**
     * @cfg {Object[]} data=[]
     */
    /**
     * @cfg {String} dataUrl=""
     */
    /**
     * @cfg {Boolean} showFiles=false
     */

    _initConfiguration($super, config) {
        $super(config);

        this._data = this._getConfigParam('data', []);
        this._dataUrl = this._getConfigParam('dataUrl', '');
        this._createFolderUrl = this._getConfigParam('createFolderUrl', '');
        this._onNodeClick = this._getConfigParam('onNodeClick', () => {});
        this._rootNodeTitle = this._getConfigParam('rootNodeTitle', '');
        this._onReload = this._getConfigParam('onReload', () => {});
        this._showFiles = this._getConfigParam('showFiles', false);
        this._filterNodes = this._getConfigParam('filterNodes', () => true);
        if (this._getConfigParam('showMessage')) {
            this._showMessage = this._getConfigParam('showMessage');
        }
    },

    _initComponentElement($super) {
        $super();
        if (null === this._data) {
            this.reload();
        } else {
            this._initTreeView();
        }
    },

    _initTreeView() {
        const container = this._componentElement.update(new Element('div', { class: 'tree-wrap' })).down();

        this._insertNode(container, {
            name: this._rootNodeTitle,
            path: '/',
            icon: '/icons/16/plesk/file-folder.png',
            htmlElement: 'div',
            cssClass: 'tree-item-root',
            isRootDirectory: true,
        });
        container.insert(this._getNodes(this._data));
    },

    _getNodes(items) {
        const ulElement = new Element('ul', { class: 'tree-container' });

        items.filter(this._filterNodes).forEach(this._insertNode.bind(this, ulElement));

        return ulElement;
    },

    _insertNode(container, item, position) {
        const element = new Element(item.htmlElement ? item.htmlElement : 'li',
            { class: `tree-item ${item.cssClass ? item.cssClass : ''}` });
        const wrapper = element.update('<div class="tree-item-wrap"></div>').down();
        element.data = item;

        const itemSelect = new Element('div', { class: 'tree-item-select' });
        wrapper.insert(itemSelect);
        this._addCommonEvents(itemSelect, item, element);
        if (item.isDirectory) {
            wrapper.insert(`<span class="tree-item-state"><img src="${Jsw.skinUrl}/images/tree-open.gif" height="16" width="16"></span>`);
            wrapper.down('.tree-item-state').observe('click', this._onNodeToggle.bind(this, item.path, element));
            wrapper.observe('dblclick', this._onNodeToggle.bind(this, item.path, element));
            wrapper.down('.tree-item-state').observe('mouseover', this._onNodeMouseover.bind(this, element));
            wrapper.down('.tree-item-state').observe('mouseleave', this._onNodeMouseleave.bind(this, element));
        } else if (!item.isRootDirectory) {
            wrapper.insert(`<span class="tree-item-state"><img src="${Jsw.skinUrl}/images/blank.gif" height="16" width="16"></span>`);
        }
        const linkElement = new Element('a', { class: 'tree-item-content', href: '#' });
        linkElement.update(`<span><img src="${Jsw.skinUrl}${item.icon}" alt=""><b>${item.name.escapeHTML()}</b></span>`);
        this._addCommonEvents(linkElement, item, element);
        wrapper.insert(linkElement);

        if (position !== 'top') {
            position = 'bottom';
        }
        const insertion = {};
        insertion[position] = element;
        container.insert(insertion);
    },

    _addCommonEvents(target, item, element) {
        target.observe('mouseover', this._onNodeMouseover.bind(this, element));
        target.observe('mouseleave', this._onNodeMouseleave.bind(this, element));
        target.observe('click', this._onNodeSelect.bindAsEventListener(this, element));
        target.observe('click', this._onNodeClick.bind(this, item.path, element));
    },

    _onNodeToggle(directoryPath, containerElement) {
        const subTreeElement = containerElement.down('ul');
        const expandElement = containerElement.down('.tree-item-state');

        if (subTreeElement) {
            expandElement.update(`<img src="${Jsw.skinUrl}/images/tree-open.gif" height="16" width="16">`);
            subTreeElement.remove();
            containerElement.data.showNewNode = false;
            return;
        }

        this._loadNode(directoryPath, containerElement);
    },

    _loadNode(directoryPath, containerElement) {
        if (containerElement.down('.tree-item-state').down('.js-loader')) {
            return;
        }
        new Ajax.Request(
            prepareUrl(this._dataUrl),
            {
                method: 'post',
                parameters: {
                    rootDir: directoryPath,
                    showFiles: this._showFiles,
                },
                onCreate: this._onNodeLoadStart.bind(this, containerElement),
                onSuccess: this._onNodeLoadSuccess.bind(this, containerElement),
                onFailure: this._onNodeLoadFailure.bind(this),
            }
        );
    },

    _isNodeExpanded(nodeElement) {
        return !!nodeElement.down('ul') || nodeElement.hasClassName('tree-item-root');
    },

    _onNodeLoadStart(containerElement) {
        const expandElement = containerElement.down('.tree-item-state');
        expandElement.update(`<img src="${Jsw.skinUrl}/images/indicator.gif" height="16" width="16" class="js-loader">`);
    },

    _onNodeLoadSuccess(containerElement, request) {
        const expandElement = containerElement.down('.tree-item-state');
        const items = request.responseText.evalJSON();
        if (items.status) {
            expandElement.update(`<img src="${Jsw.skinUrl}/images/tree-open.gif" height="16" width="16">`);
            this._showMessage(items.status, items.message);
            return;
        }

        clearStatusMessages();
        expandElement.update(`<img src="${Jsw.skinUrl}/images/tree-close.gif" height="16" width="16">`);
        containerElement.insert(this._getNodes(items));
        if (containerElement.data.showNewNode) {
            this.showNewNode(containerElement);
        }
    },

    _showMessage(status, message) {
        clearStatusMessages();
        addStatusMessage(status, message);
    },

    _onNodeSelect(event, containerElement) {
        if (event) {
            event.preventDefault();
        }
        this.resetSelection();
        containerElement.down('.tree-item-wrap').addClassName('tree-item-row-active');
        this.hideNewNode();
    },

    resetSelection() {
        this._componentElement.select('.tree-item-wrap').each(function (element) {
            element.removeClassName('tree-item-row-active');
        });
    },

    _onNodeMouseover(containerElement) {
        this.resetHover();
        containerElement.down('.tree-item-select').addClassName('tree-item-hover');
    },

    _onNodeMouseleave(containerElement) {
        this.resetHover();
        containerElement.down('.tree-item-select').removeClassName('tree-item-hover');
    },

    resetHover() {
        this._componentElement.select('.tree-item-select').each(function (element) {
            element.removeClassName('tree-item-hover');
        });
    },

    /**
     * @param {String} directory
     */
    setDirectory(directory) {
        const node = this._getNodeElement(directory);
        if (node) {
            this._onNodeSelect(null, node);
        }
    },

    reload() {
        this._componentElement.update('<div class="ajax-loading">Please wait...</div>');

        new Ajax.Request(
            prepareUrl(this._dataUrl),
            {
                method: 'post',
                parameters: { rootDir: '/', showFiles: this._showFiles },
                onSuccess: this._onFullReloadSuccess.bind(this),
                onFailure: this._onNodeLoadFailure.bind(this),
            }
        );
    },

    _onFullReloadSuccess(request) {
        this._data = request.responseText.evalJSON();
        this._initTreeView();
        this._onReload();
    },

    _onNodeLoadFailure() {
        showInternalError('Failed to load tree data.');
    },

    reloadPath(path) {
        if (path === '/') {
            this.reload();
            return;
        }

        const node = this._getNodeElement(path);
        if (!node) {
            return;
        }

        if (this._isNodeExpanded(node)) {
            node.down('ul').remove();
            this._loadNode(path, node);
        }
    },

    _getNodeElement(path) {
        const nodes = this._componentElement.select('.tree-item');
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].data.path === path) {
                return nodes[i];
            }
        }
    },

    getSelectedNode() {
        const selectedElement = this._componentElement.select('.tree-item-row-active').first();
        if (!selectedElement) {
            return null;
        }

        return selectedElement.up('.tree-item');
    },

    getSelectedItemData() {
        const selectedElement = this.getSelectedNode();
        if (!selectedElement) {
            return null;
        }

        return selectedElement.data;
    },

    expandNode(node) {
        if (!node) {
            node = this.getSelectedNode();
        }
        if (this._isNodeExpanded(node)) {
            return;
        }

        this._loadNode(node.data.path, node);
    },

    showNewNode(parentNode) {
        if (!parentNode) {
            parentNode = this.getSelectedNode();
        }
        if (!parentNode) {
            // Process root node
            parentNode = this._componentElement.select('.tree-item-root').first();
        }
        parentNode.data.showNewNode = true;
        if (!this._isNodeExpanded(parentNode)) {
            this.expandNode(parentNode);
            return;
        }
        let container = parentNode.down('ul.tree-container');
        if (!container) {
            // Process root node
            container = parentNode.next();
        }
        this._insertNewNode(container, {
            name,
            path: parentNode.data.path,
            icon: '/icons/16/plesk/file-folder.png',
            isDirectory: true,
            type: 'dir',
        }, 'top');
    },

    hideNewNode() {
        const newNode = this._componentElement.down('.js-tree-item-new');
        if (!newNode) {
            return;
        }
        let parentNode = newNode.up('.tree-item');
        if (!parentNode) {
            // Process root node
            parentNode = this._componentElement.down('.tree-item-root');
        }
        parentNode.data.showNewNode = false;
        newNode.remove();
    },

    _insertNewNode(container, item, position) {
        if (container.down('> .js-tree-item-new')) {
            return;
        }
        const ce = createElement;
        const element = ce('li.tree-item js-tree-item-new',
            {
                onrender(el) {
                    el.data = item;
                },
            },
            ce('div.tree-item-wrap',
                ce('span.tree-item-state', ce('img', { src: `${Jsw.skinUrl}/images/blank.gif` })),
                ce('div.tree-item-content',
                    ce('div.input-btn-group',
                        ce('i.icon-folder'),
                        ce('input.form-control', { type: 'text', onkeydown: this._onNewNodeKeyDown.bind(this) }),
                        ce('button.btn btn-icon-only input-btn',
                            { type: 'button', onclick: this._onCreateFolder.bind(this) }, ce('i.icon-save')),
                        ce('button.btn btn-icon-only input-btn',
                            { type: 'button', onclick: this._onCancelCreateFolder.bind(this) }, ce('i.icon-cancel'))
                    )
                )
            )
        );

        render(container, element, position);
        container.down('> .js-tree-item-new input').focus();
    },

    _onCreateFolder(e) {
        const name = e.target.up('.input-btn-group').down('.form-control').value;
        const newNode = e.target.up('.js-tree-item-new');
        let parentNode = newNode.up('.tree-item');
        if (!parentNode) {
            // Process root node
            parentNode = newNode.up('ul.tree-container').previous();
        }
        new Ajax.Request(
            prepareUrl(this._createFolderUrl),
            {
                method: 'post',
                parameters: {
                    currentDir: newNode.data.path,
                    newDirectoryName: name,
                },
                onSuccess: this._onFolderCreated.bind(this, name, parentNode),
            }
        );
    },

    _onFolderCreated(name, parentNode, transport) {
        const response = transport.responseText.evalJSON();
        const row = parentNode.up('.form-row');
        let errorMessage;
        if (response.status === 'success') {
            const path = `${parentNode.data.path}/${name}`;
            let container = parentNode.down('ul.tree-container');
            if (!container) {
                // Process root node
                container = parentNode.next();
            }
            this.hideNewNode();
            this._insertNode(container, {
                name,
                path,
                icon: '/icons/16/plesk/file-folder.png',
                isDirectory: true,
                type: 'dir',
            }, 'top');
            this._onNodeSelect(null, container);
            row.removeClassName('error');
            errorMessage = row.down('.field-value .field-errors');
            if (errorMessage) {
                errorMessage.remove();
            }
        } else {
            row.addClassName('error');
            const ce = createElement;
            errorMessage = ce('span.field-errors', ce('span.error-hint', response.message));
            render(row.down('.field-value'), errorMessage);
        }
    },

    _onCancelCreateFolder() {
        this.hideNewNode();
    },

    _onNewNodeKeyDown(e) {
        switch (e.keyCode) {
            case Event.KEY_RETURN:
                e.preventDefault();
                this._onCreateFolder(e);
                break;
            case Event.KEY_ESC:
                e.preventDefault();
                this._onCancelCreateFolder(e);
                break;
        }
    },
});



// WEBPACK FOOTER //
// ./jsw/file-manager/tree.js